<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, watch } from 'vue';
  import { Field, FieldSize } from '@/entities/searchForm';
  import { type NightsRange } from '@/shared/model';
  import NightsRangeDropdown from './NightsRangeDropdown.vue';
  import { NIGHTS_RANGE_TEXTS } from '../config/texts';
  import { normalizeRange } from '../lib/nightsRange';
  import { useNightsRange } from '../model/useNightsRange';

  interface NightsRangeFieldProps {
    modelValue?: NightsRange | null;
    checkInLabel?: string;
    summaryPrefix?: string;
    size?: `${FieldSize}`;
    loading?: boolean;
    disabled?: boolean;
    dropdownTarget?: HTMLElement | null;
  }

  const props = withDefaults(defineProps<NightsRangeFieldProps>(), {
    modelValue: null,
    checkInLabel: '',
    summaryPrefix: undefined,
    size: FieldSize.M,
    loading: false,
    disabled: false,
    dropdownTarget: null,
  });

  interface NightsRangeFieldEmits {
    (e: 'update:modelValue', range: NightsRange): void;
    (e: 'manual'): void;
    (e: 'open-change', isOpened: boolean): void;
  }

  const emit = defineEmits<NightsRangeFieldEmits>();

  const model = computed<NightsRange>({
    get: () => normalizeRange(props.modelValue),
    set: (value) => emit('update:modelValue', value),
  });

  const core = useNightsRange({
    model,
    checkInLabel: () => props.checkInLabel,
    summaryPrefix: () => props.summaryPrefix,
  });

  const onToggle = (): void => {
    core.toggle();
  };

  const onUpdateFrom = (data: number) => {
    core.setFromValue(data);
    emit('manual');
  };

  const onUpdateTo = (data: number) => {
    core.setToValue(data);
    emit('manual');
  };

  watch(core.controlActivated, (isOpened) => emit('open-change', isOpened));

  defineExpose({
    validate: (): boolean => core.validate(),
    close: (): void => core.close(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    class="nights-range-field"
    data-testid="nights-range-field"
  >
    <Field
      :label="NIGHTS_RANGE_TEXTS.fieldLabel"
      :active="core.controlActivated.value"
      :loading="loading"
      :disabled="disabled"
      :size="size"
      value-testid="nights-range-value"
      @click="onToggle"
    >
      <template #value>
        <ClientOnly>
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ core.valueLabel.value }}
          </Typo>
        </ClientOnly>
      </template>
    </Field>

    <NightsRangeDropdown
      :from="core.from.value"
      :to="core.to.value"
      :visible="core.controlActivated.value"
      :summary="core.summaryLabel.value"
      :dropdown-target="dropdownTarget"
      @update:from="onUpdateFrom"
      @update:to="onUpdateTo"
      @close="core.close"
    />
  </div>
</template>

<style scoped lang="scss">
  .nights-range-field {
    position: relative;
    width: 100%;
  }
</style>
